import { useRef, useState } from "react";
import { z } from "zod";
import { accountService } from "@miplatform/api";
import {
  CONTACT_STATUSES,
  HTTP_STATUSES,
  phoneRegex,
} from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";

export const contactValidationRules = [
  {
    name: "accountId",
    validationRule: z.string().min(1, "Account is required"),
  },
  {
    name: "firstName",
    validationRule: z.string().min(1, "First Name is required"),
  },
  {
    name: "lastName",
    validationRule: z.string().min(1, "Last Name is required"),
  },
  {
    name: "contactLevel",
    validationRule: z.string().min(1, "Contact Level is required"),
  },
  {
    name: "dependentType",
    validationRule: z.string().min(1, "Relationship Type is required"),
  },
  {
    name: "phone",
    validationRule: z
      .string()
      .regex(phoneRegex, "Phone number must be numeric and 10 digits")
      .or(z.literal("")),
  },
  {
    name: "email",
    validationRule: z.string().email("Invalid email address"),
  },
  {
    name: "preferedContactMethod",
    validationRule: z.string().min(1, "Prefered Contact Method is required"),
  },
];

export const useAddEditContactRelationViewModel = ({
  selectedContact,
  referenceIds,
  defaultContactLevel,
  onRefresh,
}) => {
  const stateManager = useComponentState();
  const contactCategoriesRef = useRef({});
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactCategories, setContactCategories] = useState([]);
  const [currentContactLevel, setCurrentContactLevel] =
    useState(defaultContactLevel);
  const [primaryRelationshipTypes, setPrimaryRelationshipTypes] = useState(
    new Set()
  );
  const isLoading = stateManager.isBusy();

  const getAccounts = () => {
    stateManager.run(async () => {
      const response = await accountService.accounts.getAll({
        sort: "name asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAccounts(
          response.data.map(({ name, id }) => ({
            label: name,
            value: id,
          }))
        );
      }
    });
  };

  const getContactCategories = (type) => {
    stateManager.run(async () => {
      const response = await accountService.contactCategories.getByType({
        type,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setCurrentContactLevel(type);
        const categoryOptions = response.data
          .filter((e) => e.isActive)
          .map(({ label }) => ({
            value: label,
            label,
          }));
        const primaryRelationshipTypes = new Set(
          response.data
            .filter(({ canBePrimary, isActive }) => canBePrimary && isActive)
            .map(({ label }) => label)
        );
        contactCategoriesRef.current = {
          ...contactCategoriesRef.current,
          [type]: { options: categoryOptions, primaryRelationshipTypes },
        };
        setContactCategories(categoryOptions);
        setPrimaryRelationshipTypes(primaryRelationshipTypes);
      }
    });
  };

  const getContactCategoriesOptions = (type) => {
    if (contactCategoriesRef.current[type]) {
      setCurrentContactLevel(type);
      setContactCategories([...contactCategoriesRef.current[type].options]);
      setPrimaryRelationshipTypes(
        new Set(contactCategoriesRef.current[type].primaryRelationshipTypes)
      );
    } else {
      getContactCategories(type);
    }
  };

  const deleteRelationOfContact = async ({ contactId, ...data }) =>
    stateManager.run(async () => {
      const response = await accountService.contacts.deleteContactRelationship({
        id: contactId,
        data,
        toastMessageConfig: {
          success: {
            text: `Contact updated successfully.`,
          },
          failure: {
            text: `Failed to updated contact.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onRefresh();
        return true;
      }
      return false;
    });

  const updateContactRelationship = ({
    accountId,
    contactId,
    label,
    isPrimary,
  }) =>
    stateManager.run(async () => {
      const response = await accountService.contacts.createContactRelationship({
        id: contactId,
        data: {
          accountId,
          label,
          isPrimary,
          RelatedReferenceId: referenceIds[currentContactLevel],
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        const didDelete = await deleteRelationOfContact({
          ...selectedContact,
          contactId,
        });

        return didDelete;
      }
      return false;
    });

  const addContactRelationship = async ({
    accountId,
    contactId,
    label,
    isPrimary,
  }) =>
    stateManager.run(async () => {
      const response = await accountService.contacts.createContactRelationship({
        id: contactId,
        data: {
          accountId,
          label,
          isPrimary,
          RelatedReferenceId: referenceIds[currentContactLevel],
        },
        toastMessageConfig: {
          success: {
            text: ` Contact relationship created successfully.`,
          },
          failure: {
            text: `Failed to create contact relationship.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onRefresh();
        return true;
      }
      return false;
    });

  const addContact = async ({ accountId, label, isPrimary, ...params }) =>
    stateManager.run(async () => {
      const response = await accountService.contacts.create({
        data: {
          ...params,
          accountId,
          status: CONTACT_STATUSES.ACTIVE,
          relationships: [
            {
              label,
              isPrimary,
              RelatedReferenceId: referenceIds[currentContactLevel],
            },
          ],
        },
        toastMessageConfig: {
          success: {
            text: ` Contact created successfully.`,
          },
          failure: {
            text: `Failed to create contact.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.CREATED) {
        onRefresh();
        getContacts();
        return true;
      }
      return false;
    });

  const getContacts = () => {
    stateManager.run(async () => {
      const response = await accountService.contacts.getAll({
        sort: "firstName asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setContacts(response.data);
      }
    });
  };

  return {
    accounts,
    contacts,
    contactCategories,
    currentContactLevel,
    primaryRelationshipTypes,
    isLoading,
    getAccounts,
    getContacts,
    getContactCategoriesOptions,
    updateContactRelationship,
    addContactRelationship,
    addContact,
  };
};
